import { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"
import { toast } from "react-toastify"
import { Navigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { useDropzone } from "react-dropzone"
import { FormattedMessage, useIntl } from "react-intl"

import {
  setJobSpec,
  setJobStatus,
  setTitle,
  storeJobSkill,
  storeJobInformation,
  storeJobCreatedAt
} from "../../../redux/slices/jobSlice"
import { addSuccessToast, addErrorToast } from "../../../redux/slices/toastSlice"
import {
  parseJobRequirementsFile,
  analyzeJobDescriptionStreaming,
  selectCandidates,
} from "../../../services/jobs"
import { usePendingToasts } from "../../../hooks/usePendingToasts"

import {
  CustomButton,
  CustomLink,
  Select,
  SignupInput,
  Spinner,
} from "../../common"

import { useWorkspaceWebSocket } from "../../../services/useWorkspaceWebSocket"
import { useTokens } from "../../../hooks/useTokens"

import { GA_EVENTS } from "../../../constants/analytics"
import { TALK_TO_US_URL } from "../../../constants"

const useProcessingJobWebSocketConnection = (jobId = null) => {
  const dispatch = useDispatch()
  const [processingJobId, setProcessingJobId] = useState(jobId);

  const jobChunkProcessedCallback = (data) => {
    if (!data?.id) return;

    if (!processingJobId) setProcessingJobId(data.id);

    if (data.information) dispatch(storeJobInformation(data));
    if (data.skill) dispatch(storeJobSkill(data));
    if (data.created_at) dispatch(storeJobCreatedAt(data));
  };

  useWorkspaceWebSocket({ connect: true, jobChunkProcessedCallback });

  return {
    processingJobId,
  }
}

const AddJobSpecPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const isMountedRef = useRef(true);

  const [loading, setLoading] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [active, setActive] = useState("paste");
  const [selectedJobPost, setSelectedJobPost] = useState(null);

  const { availableTokens, refetch: getTokens, isFetching: isFetchingTokens } = useTokens({});
  const { processingJobId } = useProcessingJobWebSocketConnection();

  const { selectedCVs, atsJobPosts } = useSelector((state) => state.jobs);
  const jobAnalysisTokenCost = useSelector((state) => state.config.tokenCosts.JOB_ANALYSIS);
  const cvs = useSelector((state) => state.cv.cvs);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    getValues,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      title: "",
      description: "",
    },
  })

  // TODO: Check if still needed
  // useEffect(() => {
  //   // Fetch job posts
  //   // If user has active integration
  //   getThirdPartyJobPosts()
  //     .then((res) => {
  //       dispatch(setAtsJobPosts(res.jobPosts))
  //     })
  //     // No need to handle. User can still create a job spec
  //     .catch(() => {})
  // }, [dispatch])

  const values = getValues()

  watch(["title", "description"])

  // When the streaming job starts to be processed, we set the processingJobId so we can move into the next page
  useEffect(() => {
    async function processJob() {
      if (processingJobId) {
        // Set selected_candidates
        await selectCandidates({
          selected_candidates: selectedCVs,
          ad_id: processingJobId,
        })

        navigate(`/job-match/${processingJobId}/requirements`)
        setLoading(false)
      }
    }

    processJob();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingJobId])

  // Cleanup effect to mark component as unmounted
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  usePendingToasts();

  const handleCheckRequirements = async () => {
    if (!values.title || !values.description) {
      return toast.error(
        formatMessage({ id: "toast.pleaseFillInJobTitleAndDescription" }),
      )
    }
    setLoading(true)

    dispatch(setJobSpec(values.description))
    dispatch(setTitle(values.title))

    try {
      dispatch(setJobStatus("PROCESSING"));

      // Handling without "await" to avoid blocking the redirection in the useEffect
      analyzeJobDescriptionStreaming({
        title: values.title,
        description: values.description,
      }).then(() => {
        // Store success toast for next page
        dispatch(addSuccessToast({
          messageId: "toast.jobDescriptionProcessedSuccessfully"
        }))
        dispatch(setJobStatus("COMPLETED"))
      }).finally(async () => {
        if (isMountedRef.current) {
          if (!processingJobId) {
            dispatch(addErrorToast({
              messageId: "toast.errorOccurred"
            }))
          }
          setLoading(false)
          await getTokens();
        }
      });
    } catch (error) {
      if (isMountedRef.current) {
        setLoading(false)
        dispatch(addErrorToast({
          message: error?.response?.data?.message || formatMessage({ id: "toast.errorOccurred" })
        }))
      }
    }
  }

  const handleImport = () => {
    if (selectedJobPost) {
      const thisPost = atsJobPosts.find(
        (jobPost) => jobPost.id === selectedJobPost.value,
      )
      setValue("title", thisPost.title)
      const description = thisPost.description

      const el = document.createElement("div")
      el.innerHTML = description

      const text = el.innerText
      setValue("description", text)

      clearErrors()
    }
  }

  const onDrop = async (acceptedFiles) => {
    try {
      setFileUploadLoading(true)

      const formData = new FormData()
      formData.append("file", acceptedFiles[0])

      const parsedJob = await parseJobRequirementsFile(formData)

      setValue("title", parsedJob.title, { shouldTouch: true })
      setValue("description", parsedJob.description, { shouldTouch: true })
      clearErrors(["title", "description"])
    } catch {
      toast.error(formatMessage({ id: "toast.errorParsingFile" }))
    } finally {
      setFileUploadLoading(false)
    }
  }

  const { getInputProps, open: openFileSelect } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      // Docx
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      // Doc
      "application/msword": [".doc"],
    },
    disabled: loading,
    multiple: false,
  })

  if (selectedCVs.length === 0) {
    return <Navigate to={"/job-match/select-cv"} />
  }

  const disabled =
    isFetchingTokens ||
    availableTokens < jobAnalysisTokenCost ||
    !values.title ||
    !values.description ||
    cvs?.length === 0 ||
    loading

  const atsJobPostOptions = atsJobPosts?.map((jobPost) => ({
    value: jobPost.id,
    label: jobPost.title,
  }))

  return (
    <div className="add-job-spec">
      <input {...getInputProps()} multiple={true} />

      <h3 className="add-job-spec__title h3">
        <FormattedMessage id="common.findThe" />{" "}
        <span>
          <FormattedMessage id="common.bestMatchedCandidates" />
        </span>
      </h3>

      <div className="add-job-spec__content">
        {atsJobPosts && (
          <div className="add-job-spec__pull-from-ats">
            <p className="h4">
              <FormattedMessage id="common.importJobPostFromGreenhouse" />
            </p>
            <div className="add-job-spec__ats-options">
              <Select
                value={selectedJobPost}
                onChange={(newValue) => setSelectedJobPost(newValue)}
                name={"job"}
                placeholder={"Job"}
                options={atsJobPostOptions}
                label={"Job Post"}
              />
              <CustomButton
                onClick={handleImport}
                className="btn primary btn--small"
                trackingEvent={GA_EVENTS.JOB_SPEC.IMPORT_FROM_ATS}
              >
                <FormattedMessage id="common.import" />
              </CustomButton>
            </div>
          </div>
        )}

        <p className="h4">
          <FormattedMessage id="common.addAJobDescription" />
        </p>

        <div className="add-job-spec__buttons">
          <CustomButton
            className={classNames({ selected: active === "paste" })}
            onClick={() => setActive("paste")}
            trackingEvent={GA_EVENTS.JOB_SPEC.PASTE_TEXT}
          >
            <FormattedMessage id="common.pasteText" />
          </CustomButton>
          <CustomButton
            className={classNames({ selected: active === "upload" })}
            onClick={() => {
              setActive("upload")
              openFileSelect?.()
            }}
            trackingEvent={GA_EVENTS.JOB_SPEC.UPLOAD}
          >
            <FormattedMessage id="common.uploadFromMyComputer" />
          </CustomButton>
        </div>

        {active === "upload" && (
          <p>
            <FormattedMessage id="common.chooseDocOrPdfFile" />
          </p>
        )}

        <div className="flex justify-center w-full">
          {fileUploadLoading && <Spinner />}
        </div>

        {!fileUploadLoading && (
          <div className="add-job-spec__form">
            <SignupInput
              register={register("title", {
                required: formatMessage({ id: "common.titleRequired" }),
                minLength: {
                  value: 5,
                  message: formatMessage({ id: "common.titleMinLength" }),
                },
              })}
              placeholder={formatMessage({ id: "common.jobTitle" })}
              error={errors.title?.message}
              touched={touchedFields.title}
              disabled={loading}
              value={values.title}
            />

            <SignupInput
              register={register("description", {
                required: formatMessage({ id: "common.descriptionRequired" }),
                minLength: {
                  value: 100,
                  message: formatMessage({ id: "common.descriptionMinLength" }),
                },
              })}
              placeholder={formatMessage({ id: "common.pasteJobDescription" })}
              error={errors.description?.message}
              touched={touchedFields.description}
              value={values.description}
              type="textarea"
              row={30}
              disabled={loading}
            />
          </div>
        )}

        {cvs?.length === 0 && (
          <p>
            <FormattedMessage id="common.pleaseUploadSomeCVsFirst" />
          </p>
        )}

        {loading && (
          <div className="w-full flex flex-col items-center">
            <Spinner />
          </div>
        )}

        <CustomButton
          onClick={handleSubmit(handleCheckRequirements)}
          className={classNames("btn primary btn--small", { disabled })}
          disabled={disabled}
          trackingEvent={GA_EVENTS.JOB_SPEC.CREATE_JOB_MATCH}
        >
          <FormattedMessage id="common.analyzeJobAd" />{" "}
          <span className="text-normal">
            <FormattedMessage
              id="common.usesXTokens"
              values={{ tokens: jobAnalysisTokenCost }}
            />
          </span>
        </CustomButton>

        <p className="add-job-spec__info">
          {availableTokens}{" "}
          <FormattedMessage id="common.tokensAvailable" />
          {" - "}
          <CustomLink
            // TODO: Uncomment this when the Premium feature is available
            // to={"/buy-more"}
            // className="add-job-spec__link"

            to={TALK_TO_US_URL}
            target="_blank"

            trackingEvent={GA_EVENTS.JOB_SPEC.GET_MORE_UPSELL}
          >
            <FormattedMessage id="common.getMore" />
          </CustomLink>
        </p>
      </div>
    </div>
  )
}

export default AddJobSpecPage
